"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.douglasPeucker = douglasPeucker;
exports.douglasPeuckerArray = douglasPeuckerArray;
exports.douglasPeuckerMultiArray = douglasPeuckerMultiArray;
exports.quantize = quantize;
exports.quantizeArray = quantizeArray;
exports.quantizeMultiArray = quantizeMultiArray;
exports.radialDistance = radialDistance;
exports.simplifyLineString = simplifyLineString;
exports.snap = snap;

var _math = require("../../math.js");

/**
 * @module ol/geom/flat/simplify
 */
// Based on simplify-js https://github.com/mourner/simplify-js
// Copyright (c) 2012, Vladimir Agafonkin
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without
// modification, are permitted provided that the following conditions are met:
//
//    1. Redistributions of source code must retain the above copyright notice,
//       this list of conditions and the following disclaimer.
//
//    2. Redistributions in binary form must reproduce the above copyright
//       notice, this list of conditions and the following disclaimer in the
//       documentation and/or other materials provided with the distribution.
//
// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
// AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
// ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
// LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
// CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
// SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
// INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
// CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
// ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
// POSSIBILITY OF SUCH DAMAGE.

/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {boolean} highQuality Highest quality.
 * @param {Array<number>} [opt_simplifiedFlatCoordinates] Simplified flat
 *     coordinates.
 * @return {Array<number>} Simplified line string.
 */
function simplifyLineString(flatCoordinates, offset, end, stride, squaredTolerance, highQuality, opt_simplifiedFlatCoordinates) {
  var simplifiedFlatCoordinates = opt_simplifiedFlatCoordinates !== undefined ? opt_simplifiedFlatCoordinates : [];

  if (!highQuality) {
    end = radialDistance(flatCoordinates, offset, end, stride, squaredTolerance, simplifiedFlatCoordinates, 0);
    flatCoordinates = simplifiedFlatCoordinates;
    offset = 0;
    stride = 2;
  }

  simplifiedFlatCoordinates.length = douglasPeucker(flatCoordinates, offset, end, stride, squaredTolerance, simplifiedFlatCoordinates, 0);
  return simplifiedFlatCoordinates;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @return {number} Simplified offset.
 */


function douglasPeucker(flatCoordinates, offset, end, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset) {
  var n = (end - offset) / stride;

  if (n < 3) {
    for (; offset < end; offset += stride) {
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset];
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset + 1];
    }

    return simplifiedOffset;
  }
  /** @type {Array<number>} */


  var markers = new Array(n);
  markers[0] = 1;
  markers[n - 1] = 1;
  /** @type {Array<number>} */

  var stack = [offset, end - stride];
  var index = 0;

  while (stack.length > 0) {
    var last = stack.pop();
    var first = stack.pop();
    var maxSquaredDistance = 0;
    var x1 = flatCoordinates[first];
    var y1 = flatCoordinates[first + 1];
    var x2 = flatCoordinates[last];
    var y2 = flatCoordinates[last + 1];

    for (var i = first + stride; i < last; i += stride) {
      var x = flatCoordinates[i];
      var y = flatCoordinates[i + 1];
      var squaredDistance_1 = (0, _math.squaredSegmentDistance)(x, y, x1, y1, x2, y2);

      if (squaredDistance_1 > maxSquaredDistance) {
        index = i;
        maxSquaredDistance = squaredDistance_1;
      }
    }

    if (maxSquaredDistance > squaredTolerance) {
      markers[(index - offset) / stride] = 1;

      if (first + stride < index) {
        stack.push(first, index);
      }

      if (index + stride < last) {
        stack.push(index, last);
      }
    }
  }

  for (var i = 0; i < n; ++i) {
    if (markers[i]) {
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset + i * stride];
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset + i * stride + 1];
    }
  }

  return simplifiedOffset;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Ends.
 * @param {number} stride Stride.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @param {Array<number>} simplifiedEnds Simplified ends.
 * @return {number} Simplified offset.
 */


function douglasPeuckerArray(flatCoordinates, offset, ends, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEnds) {
  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    simplifiedOffset = douglasPeucker(flatCoordinates, offset, end, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset);
    simplifiedEnds.push(simplifiedOffset);
    offset = end;
  }

  return simplifiedOffset;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Endss.
 * @param {number} stride Stride.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @param {Array<Array<number>>} simplifiedEndss Simplified endss.
 * @return {number} Simplified offset.
 */


function douglasPeuckerMultiArray(flatCoordinates, offset, endss, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEndss) {
  for (var i = 0, ii = endss.length; i < ii; ++i) {
    var ends = endss[i];
    var simplifiedEnds = [];
    simplifiedOffset = douglasPeuckerArray(flatCoordinates, offset, ends, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEnds);
    simplifiedEndss.push(simplifiedEnds);
    offset = ends[ends.length - 1];
  }

  return simplifiedOffset;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} squaredTolerance Squared tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @return {number} Simplified offset.
 */


function radialDistance(flatCoordinates, offset, end, stride, squaredTolerance, simplifiedFlatCoordinates, simplifiedOffset) {
  if (end <= offset + stride) {
    // zero or one point, no simplification possible, so copy and return
    for (; offset < end; offset += stride) {
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset];
      simplifiedFlatCoordinates[simplifiedOffset++] = flatCoordinates[offset + 1];
    }

    return simplifiedOffset;
  }

  var x1 = flatCoordinates[offset];
  var y1 = flatCoordinates[offset + 1]; // copy first point

  simplifiedFlatCoordinates[simplifiedOffset++] = x1;
  simplifiedFlatCoordinates[simplifiedOffset++] = y1;
  var x2 = x1;
  var y2 = y1;

  for (offset += stride; offset < end; offset += stride) {
    x2 = flatCoordinates[offset];
    y2 = flatCoordinates[offset + 1];

    if ((0, _math.squaredDistance)(x1, y1, x2, y2) > squaredTolerance) {
      // copy point at offset
      simplifiedFlatCoordinates[simplifiedOffset++] = x2;
      simplifiedFlatCoordinates[simplifiedOffset++] = y2;
      x1 = x2;
      y1 = y2;
    }
  }

  if (x2 != x1 || y2 != y1) {
    // copy last point
    simplifiedFlatCoordinates[simplifiedOffset++] = x2;
    simplifiedFlatCoordinates[simplifiedOffset++] = y2;
  }

  return simplifiedOffset;
}
/**
 * @param {number} value Value.
 * @param {number} tolerance Tolerance.
 * @return {number} Rounded value.
 */


function snap(value, tolerance) {
  return tolerance * Math.round(value / tolerance);
}
/**
 * Simplifies a line string using an algorithm designed by Tim Schaub.
 * Coordinates are snapped to the nearest value in a virtual grid and
 * consecutive duplicate coordinates are discarded.  This effectively preserves
 * topology as the simplification of any subsection of a line string is
 * independent of the rest of the line string.  This means that, for examples,
 * the common edge between two polygons will be simplified to the same line
 * string independently in both polygons.  This implementation uses a single
 * pass over the coordinates and eliminates intermediate collinear points.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {number} end End.
 * @param {number} stride Stride.
 * @param {number} tolerance Tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @return {number} Simplified offset.
 */


function quantize(flatCoordinates, offset, end, stride, tolerance, simplifiedFlatCoordinates, simplifiedOffset) {
  // do nothing if the line is empty
  if (offset == end) {
    return simplifiedOffset;
  } // snap the first coordinate (P1)


  var x1 = snap(flatCoordinates[offset], tolerance);
  var y1 = snap(flatCoordinates[offset + 1], tolerance);
  offset += stride; // add the first coordinate to the output

  simplifiedFlatCoordinates[simplifiedOffset++] = x1;
  simplifiedFlatCoordinates[simplifiedOffset++] = y1; // find the next coordinate that does not snap to the same value as the first
  // coordinate (P2)

  var x2, y2;

  do {
    x2 = snap(flatCoordinates[offset], tolerance);
    y2 = snap(flatCoordinates[offset + 1], tolerance);
    offset += stride;

    if (offset == end) {
      // all coordinates snap to the same value, the line collapses to a point
      // push the last snapped value anyway to ensure that the output contains
      // at least two points
      // FIXME should we really return at least two points anyway?
      simplifiedFlatCoordinates[simplifiedOffset++] = x2;
      simplifiedFlatCoordinates[simplifiedOffset++] = y2;
      return simplifiedOffset;
    }
  } while (x2 == x1 && y2 == y1);

  while (offset < end) {
    // snap the next coordinate (P3)
    var x3 = snap(flatCoordinates[offset], tolerance);
    var y3 = snap(flatCoordinates[offset + 1], tolerance);
    offset += stride; // skip P3 if it is equal to P2

    if (x3 == x2 && y3 == y2) {
      continue;
    } // calculate the delta between P1 and P2


    var dx1 = x2 - x1;
    var dy1 = y2 - y1; // calculate the delta between P3 and P1

    var dx2 = x3 - x1;
    var dy2 = y3 - y1; // if P1, P2, and P3 are colinear and P3 is further from P1 than P2 is from
    // P1 in the same direction then P2 is on the straight line between P1 and
    // P3

    if (dx1 * dy2 == dy1 * dx2 && (dx1 < 0 && dx2 < dx1 || dx1 == dx2 || dx1 > 0 && dx2 > dx1) && (dy1 < 0 && dy2 < dy1 || dy1 == dy2 || dy1 > 0 && dy2 > dy1)) {
      // discard P2 and set P2 = P3
      x2 = x3;
      y2 = y3;
      continue;
    } // either P1, P2, and P3 are not colinear, or they are colinear but P3 is
    // between P3 and P1 or on the opposite half of the line to P2.  add P2,
    // and continue with P1 = P2 and P2 = P3


    simplifiedFlatCoordinates[simplifiedOffset++] = x2;
    simplifiedFlatCoordinates[simplifiedOffset++] = y2;
    x1 = x2;
    y1 = y2;
    x2 = x3;
    y2 = y3;
  } // add the last point (P2)


  simplifiedFlatCoordinates[simplifiedOffset++] = x2;
  simplifiedFlatCoordinates[simplifiedOffset++] = y2;
  return simplifiedOffset;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<number>} ends Ends.
 * @param {number} stride Stride.
 * @param {number} tolerance Tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @param {Array<number>} simplifiedEnds Simplified ends.
 * @return {number} Simplified offset.
 */


function quantizeArray(flatCoordinates, offset, ends, stride, tolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEnds) {
  for (var i = 0, ii = ends.length; i < ii; ++i) {
    var end = ends[i];
    simplifiedOffset = quantize(flatCoordinates, offset, end, stride, tolerance, simplifiedFlatCoordinates, simplifiedOffset);
    simplifiedEnds.push(simplifiedOffset);
    offset = end;
  }

  return simplifiedOffset;
}
/**
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset Offset.
 * @param {Array<Array<number>>} endss Endss.
 * @param {number} stride Stride.
 * @param {number} tolerance Tolerance.
 * @param {Array<number>} simplifiedFlatCoordinates Simplified flat
 *     coordinates.
 * @param {number} simplifiedOffset Simplified offset.
 * @param {Array<Array<number>>} simplifiedEndss Simplified endss.
 * @return {number} Simplified offset.
 */


function quantizeMultiArray(flatCoordinates, offset, endss, stride, tolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEndss) {
  for (var i = 0, ii = endss.length; i < ii; ++i) {
    var ends = endss[i];
    var simplifiedEnds = [];
    simplifiedOffset = quantizeArray(flatCoordinates, offset, ends, stride, tolerance, simplifiedFlatCoordinates, simplifiedOffset, simplifiedEnds);
    simplifiedEndss.push(simplifiedEnds);
    offset = ends[ends.length - 1];
  }

  return simplifiedOffset;
}
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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